// Radha — the 13-week behavioural cash forecast (080). CFO track #1.
//
// Weekly bars = expected receipts from each debtor's OBSERVED payment lag;
// the shaded band is the cumulative optimistic/pessimistic envelope from
// each debtor's own variance. The dashed line is the contractual (due-date)
// fantasy, kept on the chart deliberately: the gap between it and the bars
// IS the message.
//
// The number the model refuses to fake sits above the chart: receipts from
// invoices overdue beyond even pessimistic behaviour are "timing unknown",
// not silently crammed into week 1.
import React, { useMemo } from "react";
import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from "recharts";

import { useCashForecast } from "../hooks/useGlobalDesk";
import { useMe } from "../lib/authSupabase";
import { useViewCountry } from "../lib/jurisdiction";
import { fmtMoney, fmtDateShort } from "../lib/money";

export function CashForecastPanel() {
  const me = useMe(true);
  const viewCountry = useViewCountry(me?.tenant_country);
  const { data, isLoading } = useCashForecast(viewCountry);

  const model = useMemo(() => {
    const rows = data ?? [];
    const weeks = rows.filter((r) => r.week_index >= 0 && r.week_index <= 12);
    const unknown = rows.find((r) => r.week_index === -1);
    const beyond = rows.find((r) => r.week_index === 99);
    let cumC = 0, cumL = 0, cumH = 0;
    const chart = weeks.map((w) => {
      cumC += Number(w.expected); cumL += Number(w.low ?? 0); cumH += Number(w.high ?? 0);
      return {
        label: fmtDateShort(w.week_start!),
        weekly: Number(w.expected),
        contractual: Number(w.contractual ?? 0),
        cumulative: cumC,
        bandLow: cumL,
        bandSpread: Math.max(cumH - cumL, 0),
        promised: Number(w.promise_backed ?? 0),
        n: Number(w.invoice_count),
      };
    });
    const total = cumC;
    const promisedTotal = weeks.reduce((s, w) => s + Number(w.promise_backed ?? 0), 0);
    // The cumulative envelope converges by construction at the horizon (same
    // money, different timing) — the uncertainty lives mid-horizon, so the
    // tile reports the widest cumulative gap and when it occurs.
    let bandAt = chart[0];
    for (const c of chart) if (c.bandSpread > (bandAt?.bandSpread ?? 0)) bandAt = c;
    return {
      chart, total,
      bandAt,
      promisedTotal,
      unknownAmt: Number(unknown?.expected ?? 0), unknownN: Number(unknown?.invoice_count ?? 0),
      beyondAmt: Number(beyond?.expected ?? 0),
    };
  }, [data]);

  if (isLoading) return null;
  if (model.chart.length === 0) return null;

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 22 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>13-week cash forecast</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 12px", maxWidth: 780 }}>
        Receipts projected from each debtor's <em>observed</em> payment behaviour — not due dates. The band is the
        cumulative envelope from each debtor's own variance; the dashed line is what due dates claim, and the gap
        between them is the working-capital conversation.
      </p>

      <div style={{ display: "flex", gap: 24, margin: "0 0 14px", flexWrap: "wrap" }}>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Expected in 13 weeks</div>
          <div style={{ font: "600 20px Inter" }}>{fmtMoney(model.total)}</div></div>
        {model.bandAt && model.bandAt.bandSpread > 0 && (
          <div title="The widest cumulative optimistic-pessimistic gap over the horizon. The envelope converges by the final week — same money, uncertain timing.">
            <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Widest envelope (wk of {model.bandAt.label})</div>
            <div style={{ font: "600 20px Inter", color: "var(--fg-muted)" }}>
              {fmtMoney(model.bandAt.bandLow)} – {fmtMoney(model.bandAt.bandLow + model.bandAt.bandSpread)}</div></div>
        )}
        <div title="Explicitly promised by debtors on collection calls — an overlay on the behavioural number, never added to it.">
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Promise-backed</div>
          <div style={{ font: "600 20px Inter", color: "var(--status-good)" }}>{fmtMoney(model.promisedTotal)}</div></div>
        {model.unknownAmt > 0 && (
          <div title="Overdue beyond even the debtor's pessimistic behavioural window. The model has no honest basis for a week, so it refuses to assign one — this is collections/legal work, not forecastable cash.">
            <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Timing unknown ⚠</div>
            <div style={{ font: "600 20px Inter", color: "var(--status-warning)" }}>
              {fmtMoney(model.unknownAmt)}
              <span style={{ fontSize: 12, fontWeight: 400, color: "var(--fg-muted)" }}> · {model.unknownN} invoices</span>
            </div></div>
        )}
        {model.beyondAmt > 0 && (
          <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Beyond 13 weeks</div>
            <div style={{ font: "600 20px Inter", color: "var(--fg-muted)" }}>{fmtMoney(model.beyondAmt)}</div></div>
        )}
      </div>

      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <ComposedChart data={model.chart} margin={{ top: 6, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--fg-muted)" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v: number) => fmtMoney(v)} tick={{ fontSize: 11, fill: "var(--fg-muted)" }}
              axisLine={false} tickLine={false} width={62} />
            <Tooltip
              formatter={(v: number, name: string) =>
                name === "band" ? null : [fmtMoney(v),
                  { weekly: "Expected (week)", contractual: "Contractual (week)", cumulative: "Cumulative expected" }[name] ?? name]}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--fg)" }}
              labelStyle={{ color: "var(--fg)" }} itemStyle={{ color: "var(--fg)" }} />
            <Legend formatter={(v: string) =>
              ({ weekly: "Weekly expected", contractual: "Contractual", cumulative: "Cumulative", bandSpread: "Confidence band" }[v] ?? v)}
              wrapperStyle={{ fontSize: 12 }} />
            {/* cumulative confidence band: transparent base + shaded spread */}
            <Area dataKey="bandLow" stackId="band" stroke="none" fill="transparent" legendType="none" tooltipType="none" isAnimationActive={false} />
            <Area dataKey="bandSpread" stackId="band" stroke="none" fill="var(--accent-purple)" fillOpacity={0.12} isAnimationActive={false} />
            <Bar dataKey="weekly" fill="var(--accent-purple)" fillOpacity={0.85} radius={[4, 4, 0, 0]} maxBarSize={34} />
            <Line dataKey="cumulative" stroke="var(--accent-purple)" strokeWidth={2} dot={false} />
            <Line dataKey="contractual" stroke="var(--fg-faint)" strokeWidth={1.4} strokeDasharray="5 4" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p style={{ color: "var(--fg-faint)", fontSize: 11, margin: "8px 0 0" }}>
        Method: receipt date = due date + debtor's mean observed lag; band = ± one standard deviation of that debtor's
        own history, tenant median where history is thin. Weekly promise amounts overlay the same invoices — they are
        never double-counted. Converted at carrying rates.
      </p>
    </div>
  );
}
